/**
 * Agent Universe Lighting Configurations
 * Sport-specific lighting presets for baseball stadium and basketball arena atmospheres
 */

import * as THREE from 'three';

/**
 * Baseball Stadium Lighting
 * Night game atmosphere with sodium orange stadium lights
 * Inspired by mlbmeetings-animation-experiments.html lines 2071-2101
 */
export const baseballLighting = {
  ambient: {
    color: 0x606060,
    intensity: 0.6,
  },
  hemisphere: {
    skyColor: 0xffffff,
    groundColor: 0x444422,
    intensity: 0.4,
  },
  primary: {
    color: 0xFFD400, // SLAM yellow
    intensity: 1.5,
    distance: 600,
    position: new THREE.Vector3(0, 80, 0),
  },
  secondary: {
    color: 0x0C92F1, // SLAM blue accent
    intensity: 0.5,
    distance: 400,
    position: new THREE.Vector3(-100, -30, 100),
  },
  stadium: {
    color: 0xffa340, // Sodium orange - night stadium glow
    intensity: 0.8,
    distance: 600,
    angle: Math.PI / 4,
    penumbra: 0.5,
    positions: [
      // 8 stadium lights positioned around the scene
      new THREE.Vector3(200, 200, 200),   // Front-right
      new THREE.Vector3(-200, 200, 200),  // Front-left
      new THREE.Vector3(200, 200, -200),  // Back-right
      new THREE.Vector3(-200, 200, -200), // Back-left
      new THREE.Vector3(280, 150, 0),     // Right side
      new THREE.Vector3(-280, 150, 0),    // Left side
      new THREE.Vector3(0, 150, 280),     // Front center
      new THREE.Vector3(0, 150, -280),    // Back center
    ],
  },
  fog: {
    color: 0x000000,
    density: 0.003,
  },
};

/**
 * Basketball Arena Lighting
 * Warm tungsten lighting with arena-style arrangement
 */
export const basketballLighting = {
  ambient: {
    color: 0x707070,
    intensity: 0.7,
  },
  hemisphere: {
    skyColor: 0xffffff,
    groundColor: 0x443333,
    intensity: 0.45,
  },
  primary: {
    color: 0xFF6B00, // Basketball orange
    intensity: 1.4,
    distance: 550,
    position: new THREE.Vector3(0, 90, 0),
  },
  secondary: {
    color: 0x3b82f6, // Blue accent for contrast
    intensity: 0.4,
    distance: 350,
    position: new THREE.Vector3(-80, -20, 80),
  },
  stadium: {
    color: 0xff9933, // Warm tungsten - arena glow
    intensity: 0.9,
    distance: 550,
    angle: Math.PI / 4,
    penumbra: 0.6,
    positions: [
      // 8 arena lights in circular arrangement
      new THREE.Vector3(180, 180, 180),
      new THREE.Vector3(-180, 180, 180),
      new THREE.Vector3(180, 180, -180),
      new THREE.Vector3(-180, 180, -180),
      new THREE.Vector3(250, 140, 0),
      new THREE.Vector3(-250, 140, 0),
      new THREE.Vector3(0, 140, 250),
      new THREE.Vector3(0, 140, -250),
    ],
  },
  fog: {
    color: 0x0a0a0c,
    density: 0.0025,
  },
};

/**
 * Get lighting configuration based on sport type
 * @param {boolean} isBasketball - true for basketball, false for baseball
 * @returns {Object} Lighting configuration
 */
export function getLightingConfig(isBasketball) {
  return isBasketball ? basketballLighting : baseballLighting;
}

/**
 * Create and add all lights to a Three.js scene
 * @param {THREE.Scene} scene - Three.js scene
 * @param {Object} config - Lighting configuration
 * @param {number} [teamPrimaryColor] - Optional team primary color
 * @returns {THREE.Light[]} Array of created lights
 */
export function createSceneLighting(scene, config, teamPrimaryColor) {
  const lights = [];

  // Ambient light
  const ambient = new THREE.AmbientLight(config.ambient.color, config.ambient.intensity);
  scene.add(ambient);
  lights.push(ambient);

  // Hemisphere light for natural fill
  const hemi = new THREE.HemisphereLight(
    config.hemisphere.skyColor,
    config.hemisphere.groundColor,
    config.hemisphere.intensity
  );
  scene.add(hemi);
  lights.push(hemi);

  // Primary point light (centered above)
  const primaryColor = teamPrimaryColor ?? config.primary.color;
  const primary = new THREE.PointLight(primaryColor, config.primary.intensity, config.primary.distance);
  primary.position.copy(config.primary.position);
  scene.add(primary);
  lights.push(primary);

  // Secondary point light (accent)
  const secondary = new THREE.PointLight(
    config.secondary.color,
    config.secondary.intensity,
    config.secondary.distance
  );
  secondary.position.copy(config.secondary.position);
  scene.add(secondary);
  lights.push(secondary);

  // Stadium spotlights (8 lights positioned around the scene)
  config.stadium.positions.forEach((pos, index) => {
    // Vary intensity for front/back lights
    const intensityMultiplier = index < 4 ? 1.0 : 0.6;
    const spotlight = new THREE.SpotLight(
      config.stadium.color,
      config.stadium.intensity * intensityMultiplier,
      config.stadium.distance,
      config.stadium.angle,
      config.stadium.penumbra
    );
    spotlight.position.copy(pos);
    spotlight.target.position.set(0, 0, 0);
    scene.add(spotlight);
    scene.add(spotlight.target);
    lights.push(spotlight);
  });

  // Scene fog for depth
  scene.fog = new THREE.FogExp2(config.fog.color, config.fog.density);

  return lights;
}

/**
 * Update lighting colors dynamically (for team color theming)
 * @param {THREE.Light[]} lights - Array of lights
 * @param {number} primaryColor - Primary color hex
 * @param {number} [stadiumColor] - Optional stadium color
 */
export function updateLightingColors(lights, primaryColor, stadiumColor) {
  lights.forEach((light) => {
    if (light instanceof THREE.PointLight && light.position.y > 50) {
      // Primary overhead light - use team color
      light.color.setHex(primaryColor);
    } else if (light instanceof THREE.SpotLight && stadiumColor) {
      // Stadium spotlights
      light.color.setHex(stadiumColor);
    }
  });
}

export default { baseballLighting, basketballLighting, getLightingConfig, createSceneLighting, updateLightingColors };
